import { memo, useState } from "react";
import { formatDistanceToNow, format } from "date-fns";
import { Loader2Icon, LogOutIcon, MonitorSmartphoneIcon } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUser } from "@/hooks/useUserProvider";
import {
  ActiveSession,
  SecurityRequestError,
  useRevokeOtherSessions,
  useRevokeSession,
  useSessions,
} from "@/hooks/queries/securityQueries";
import { cn } from "@/lib/utils";
import { SecuritySection } from "./Section";
import { providerName, sessionIcon, sessionTitle } from "./describe";

const SessionRow = memo(
  ({
    session,
    onSignOut,
    busy,
  }: {
    session: ActiveSession;
    onSignOut: (session: ActiveSession) => void;
    busy: boolean;
  }) => {
    const Icon = sessionIcon(session);
    const lastActive = formatDistanceToNow(new Date(session.lastUsedAt), {
      addSuffix: true,
    });
    const signedIn = format(new Date(session.createdAt), "MMM d, yyyy");

    return (
      <li
        className={cn(
          "flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between",
          session.current
            ? "border-emerald-500/30 bg-emerald-500/5"
            : "border-border/60",
        )}
      >
        <div className="flex min-w-0 items-start gap-3">
          <div className="bg-secondary relative mt-0.5 shrink-0 rounded-md p-2">
            <Icon className="size-4 text-white" aria-hidden="true" />
          </div>
          <div className="min-w-0 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-medium text-white">
                {sessionTitle(session)}
              </span>
              {session.current ? (
                <Badge className="gap-1 border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0 text-[11px] text-emerald-300 hover:bg-emerald-500/10">
                  <span className="relative flex size-1.5">
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex size-1.5 rounded-full bg-emerald-400" />
                  </span>
                  This device
                </Badge>
              ) : null}
            </div>
            <p className="text-muted-foreground text-xs">
              {[
                session.ipAddress ? `IP ${session.ipAddress}` : null,
                session.current ? "Active now" : `Last active ${lastActive}`,
                `Signed in ${signedIn} with ${providerName(session.loginMethod)}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        </div>
        {session.current ? null : (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive self-start sm:self-center"
            disabled={busy}
            onClick={() => onSignOut(session)}
          >
            {busy ? <Loader2Icon className="animate-spin" /> : <LogOutIcon />}
            Sign out
          </Button>
        )}
      </li>
    );
  },
);
SessionRow.displayName = "SessionRow";

export const SessionsSection = ({
  onReauthRequired,
}: {
  onReauthRequired: (retry: () => void, reason: string) => void;
}) => {
  const { user, logout } = useUser();
  const { data: sessions, isLoading, isError, error, refetch } = useSessions();
  const revokeSession = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();
  const [pending, setPending] = useState<ActiveSession | null>(null);
  const [confirmOthers, setConfirmOthers] = useState(false);

  const others = sessions?.filter(session => !session.current) ?? [];

  const signOutOne = async (session: ActiveSession) => {
    try {
      await revokeSession.mutateAsync(session.id);
      toast.success("Session signed out");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not sign out");
    } finally {
      setPending(null);
    }
  };

  const signOutOthers = async () => {
    try {
      const revoked = await revokeOthers.mutateAsync();
      toast.success(
        revoked === 1
          ? "Signed out 1 other session"
          : `Signed out ${revoked} other sessions`,
      );
    } catch (err) {
      if (
        err instanceof SecurityRequestError &&
        err.code === "REAUTH_REQUIRED"
      ) {
        if (user?.hasCustomPassword === false) {
          toast.info("Sign in again to continue", {
            description:
              "For your security, sign in again and then sign out your other sessions.",
            action: { label: "Sign in again", onClick: () => logout() },
          });
        } else {
          onReauthRequired(
            () => void signOutOthers(),
            "Enter your password to sign out every other session.",
          );
        }
        return;
      }
      toast.error(err instanceof Error ? err.message : "Could not sign out");
    }
  };

  return (
    <SecuritySection
      icon={MonitorSmartphoneIcon}
      title="Where you're signed in"
      description="Sessions stay signed in for up to 30 days, or 14 days without use. Sign out any you don't recognise."
      action={
        others.length ? (
          <Button
            size="sm"
            variant="outline"
            className="text-destructive hover:text-destructive"
            disabled={revokeOthers.isPending}
            onClick={() => setConfirmOthers(true)}
          >
            {revokeOthers.isPending ? (
              <Loader2Icon className="animate-spin" />
            ) : (
              <LogOutIcon />
            )}
            Sign out all other sessions
          </Button>
        ) : null
      }
    >
      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 rounded-lg" />
          <Skeleton className="h-16 rounded-lg" />
        </div>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-4">
            {error.message}
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <ul className="flex flex-col gap-2">
          {sessions?.map(session => (
            <SessionRow
              key={session.id}
              session={session}
              busy={revokeSession.isPending && pending?.id === session.id}
              onSignOut={setPending}
            />
          ))}
          {!others.length ? (
            <li className="text-muted-foreground px-1 text-xs">
              This is the only device signed in to your account.
            </li>
          ) : null}
        </ul>
      )}

      <AlertDialog
        open={!!pending}
        onOpenChange={open =>
          !open && !revokeSession.isPending && setPending(null)
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out this session?</AlertDialogTitle>
            <AlertDialogDescription>
              {pending ? sessionTitle(pending) : ""} will need to sign in again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => pending && void signOutOne(pending)}
            >
              Sign out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmOthers} onOpenChange={setConfirmOthers}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out everywhere else?</AlertDialogTitle>
            <AlertDialogDescription>
              Every browser, extension and connected app except this one will be
              signed out of UniDeck.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void signOutOthers()}
            >
              Sign out others
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SecuritySection>
  );
};
